// @flow

import type { Store } from 'redux';
import { isEmpty, cloneDeep } from 'lodash';

import { fetchQvt, llEventTransition } from 'Common/player/actions';
import { CastReceiver, ChromeCastReceiver } from 'Browser/cast/receiver';
import { getFreeWheelAd as getLiveLinearFreeWheelAd } from 'Common/lib/player/managers/LiveLinearAdManager';
import { RECEIVER_APP_PATH } from 'Common/cast/CastUtils';

import type { State, BaseAction, ThunkAction, Dispatch } from 'Common/types';
import type { Cast } from 'Common/cast/types';
import AdSlotInfo from './AdSlotInfo';

declare var cast: Cast;

export const TRANSITIONS = {
  LL_TRANSITION: 'LL_TRANSITION',
  LL_TO_AD_TRANSITION: 'LL_TO_AD_TRANSITION',
  AD_TO_AD_TRANSITION: 'AD_TO_AD_TRANSITION',
  AD_TO_LL_TRANSITION: 'AD_TO_LL_TRANSITION',
};
const NEXT_MEDIA_SCHEDULE_THRESHOLD = 10;

const GEO_ZIP_URL = 'https://p-geo.movetv.com/geo';

export class LiveLinearPlaybackManager {
  store: Store<State, BaseAction | ThunkAction, Dispatch>;
  chromeCastReceiver: ChromeCastReceiver;
  queueManager: cast.framework.QueueManager;
  ottMappingId: string;
  dropQvtSubscription: () => {};
  dropNextQvtSubscription: () => {};
  dropAdInfoSubscription: () => {};
  oneSecondTimer: IntervalID;
  geoZipCode: string;

  currentContentDurationS: number | void;
  currentAdDurationS: number | void;
  nextQvtUrl: string | void;
  isNextItemScheduled = false;
  isLiveDone = false;
  hideTimeline = false;
  numberOfAdSlots = 0;
  checkNextAdSlotTimeoutId: TimeoutID;
  nextAdSlotAdIndex: number;
  nextAdStartTimeRelativeS: number | void;
  currentEventMediaItem: cast.framework.messages.MediaInformation;
  nextEventMediaItem: cast.framework.messages.MediaInformation;
  nextAdSlotsInfo: AdSlotInfo | void;

  constructor(
    store: Store<State, BaseAction | ThunkAction, Dispatch>,
    receiver: ChromeCastReceiver,
  ) {
    this.store = store;
    this.chromeCastReceiver = receiver;
    this.queueManager = receiver.playerManager.getQueueManager();
  }

  start(ottMappingId: string, currentEventMediaItem: cast.framework.messages.MediaInformation) {
    this._resetState();
    this._unsubscribeFromStoreUpdates();

    this._subscribeToStoreUpdates();
    this._scheduleOneSecondTimer();
    this.ottMappingId = ottMappingId;
    this.currentEventMediaItem = cloneDeep(currentEventMediaItem);
    this.hideTimeline = false;
    if (!this.geoZipCode) this._getGeoZip();
  }

  stop() {
    this._resetState();
    this._unsubscribeFromStoreUpdates();
    this._stopOneSecondTimer();
  }

  get isPlayingAd() {
    return this.currentAdDurationS !== undefined;
  }

  get isNextAdSlotEmpty() {
    return isEmpty(this.nextAdSlotMediaUrls);
  }

  get isTimelineHidden() {
    return this.hideTimeline;
  }

  get nextAdSlotMediaDurations() {
    return !this.nextAdSlotsInfo ? [] : this.nextAdSlotsInfo.adSlotMediaDurations || [];
  }

  get nextAdSlotMediaUrls() {
    return !this.nextAdSlotsInfo ? [] : this.nextAdSlotsInfo.adSlotMediaUrls || [];
  }

  get nextAdSlotAdsNumber() {
    return !this.nextAdSlotsInfo ? 0 : this.nextAdSlotsInfo.adsNumber || 0;
  }

  onMediaFinished = () => {
    CastReceiver.log(`[LL Manager] MEDIA_FINISHED received.`);
    if (this.isPlayingAd) {
      // This is Ad -> Ad or Ad -> Event transition
      this._onCurrentAdFinished();
    } else {
      // This is Event -> Event transition
      this._onCurrentEventFinished();
    }
  };

  onPlaying = () => {
    this.hideTimeline = this.isPlayingAd;
  };

  liveDone = () => {
    CastReceiver.log(`[LL Manager] LIVE_DONE received.`);
    this.isLiveDone = true;
  };

  updateMediaStatus = (mediaStatusData: cast.framework.messages.MediaStatus) => {
    if (this.isPlayingAd) {
      // mediaStatusData.currentTime = 0;
      if (
        mediaStatusData.playerState === cast.framework.messages.PlayerState.IDLE &&
        mediaStatusData.extendedStatus
      ) {
        mediaStatusData.playerState = cast.framework.messages.PlayerState.PLAYING;
      }
    } else {
      // Placeholder
    }
    if (this.isTimelineHidden) {
      mediaStatusData.extendedStatus = undefined;
    }
    this.updateMediaInformation(mediaStatusData.media, mediaStatusData);
  };

  updateMediaInformation = (
    media: cast.framework.messages.MediaInformation,
    mediaStatusData: cast.framework.messages.MediaStatus,
  ) => {
    if (typeof media !== 'object') return;
    if (this.isPlayingAd) {
      // mediaStatusData.media.duration = 0; // this.liveLinearPlaybackManager.currentContentDurationS;
      media.metadata = {
        ...this.currentEventMediaItem.metadata,
        title: `${this.currentEventMediaItem.metadata.title} (Ad ${this.nextAdSlotAdIndex} of ${
          this.nextAdSlotAdsNumber
        })`,
      };
      // mediaStatusData.media.streamType = cast.framework.messages.StreamType.LIVE;
    } else {
      media.duration = this.isLiveDone
        ? this.currentContentDurationS
        : mediaStatusData?.liveSeekableRange?.end ||
          this.chromeCastReceiver.playerManager.getLiveSeekableRange()?.end;
    }
  };

  get currentPosition() {
    return this.chromeCastReceiver.playerManager.getCurrentTimeSec();
  }

  _subscribeToStoreUpdates = () => {
    this.dropQvtSubscription = CastReceiver.observeStore(
      this.store,
      ['player.qvtPayload._next', 'player.qvtPayload.playback_info.clips'],
      this._onQvtChange,
    );
    this.dropNextQvtSubscription = CastReceiver.observeStore(
      this.store,
      ['player.nextQvtPayload.self'],
      this._onNextQvtChange,
    );
    this.dropAdInfoSubscription = CastReceiver.observeStore(
      this.store,
      ['player.qvtPayload.playback_info.ad_info'],
      this._onAdInfoChange,
    );
  };

  _unsubscribeFromStoreUpdates = () => {
    if (typeof this.dropQvtSubscription === 'function') this.dropQvtSubscription();
    if (typeof this.dropNextQvtSubscription === 'function') this.dropNextQvtSubscription();
    if (typeof this.dropAdInfoSubscription === 'function') this.dropAdInfoSubscription();
  };

  _onQvtChange = (nextState: Object) => {
    if (isEmpty(nextState)) return;
    const {
      player: {
        qvtPayload: {
          _next: nextQvtUrl,
          playback_info: { clips },
        },
      },
    } = this.store.getState();
    this.currentContentDurationS = clips.reduce(
      (acc, { start_offset: startOffset, stop_offset: stopOffset }) =>
        acc + stopOffset - startOffset,
      0,
    );
    this.nextQvtUrl = nextQvtUrl;
  };

  _onNextQvtChange = async (nextState: Object) => {
    if (isEmpty(nextState)) return;
    const {
      player: {
        nextQvtPayload: {
          playback_info: { m3u8_url_template: hlsUrlTemplate },
        },
      },
    } = this.store.getState();
    const hlsUrl = hlsUrlTemplate.replace(
      /\$version\$_\$target_duration\$_\$encryption_type\$_\$audio_type\$/,
      '7_3_widevine_aac',
    );
    CastReceiver.log(`[LL Manager] Scheduling the next Event from the NEXT QVT response.`);
    const mediaItem = await this.chromeCastReceiver.scheduleQueueItem(
      hlsUrl,
      TRANSITIONS.LL_TRANSITION,
    );
    this.nextEventMediaItem = cloneDeep(mediaItem);
  };

  _resetState = () => {
    this.currentContentDurationS = undefined;
    this.currentAdDurationS = undefined;
    this.nextQvtUrl = undefined;
    this.isNextItemScheduled = false;
    this.isLiveDone = false;
    this.numberOfAdSlots = 0;
    this.nextAdSlotAdIndex = 0;
    this.nextAdSlotsInfo = undefined;
    this.nextAdStartTimeRelativeS = undefined;
    clearTimeout(this.checkNextAdSlotTimeoutId);
  };

  _stopOneSecondTimer = () => {
    clearInterval(this.oneSecondTimer);
  };

  _scheduleOneSecondTimer = () => {
    this._stopOneSecondTimer();
    this.oneSecondTimer = setInterval(this._onTimerTick, 1000);
  };

  _onCurrentAdFinished = () => {
    this.currentAdDurationS = this.nextAdSlotMediaDurations.shift();
    if (this.currentAdDurationS !== undefined) {
      // Ad -> Ad transition
      CastReceiver.log(
        `[LL Manager] Triggering Ad -> Ad transition. Ad duration=${this.currentAdDurationS}`,
      );
      this.nextAdStartTimeRelativeS += this.currentAdDurationS;
      this.nextAdSlotAdIndex += 1;
    } else {
      // Ad -> Event transition
      CastReceiver.log(`[LL Manager] Triggering Ad -> Event transition.`);
      this.nextAdStartTimeRelativeS = undefined;
    }
    this.isNextItemScheduled = false;
  };

  _onCurrentEventFinished = () => {
    this._resetState();
    this.currentEventMediaItem = this.nextEventMediaItem;
    this.nextEventMediaItem = undefined;
    this.store.dispatch(llEventTransition());
  };

  _removeCurrentQueueItem = () => {
    this.hideTimeline = true;
    this.queueManager.removeItems([this.queueManager.getCurrentItem().itemId]);
  };

  _onTimerTick = () => {
    if (this.isPlayingAd) {
      const { currentPosition } = this;
      if (
        // Check isNextItemScheduled to avoid potential double transition
        // if this block is executed after the onMediaFinished call
        this.isNextItemScheduled === true &&
        this.currentAdDurationS !== undefined &&
        currentPosition >= this.currentAdDurationS
      ) {
        // Trigger ad -> ad or ad -> event transition
        this._removeCurrentQueueItem();
        this._onCurrentAdFinished();
      } else if (
        this.isNextItemScheduled === false &&
        this.currentAdDurationS !== undefined &&
        currentPosition + NEXT_MEDIA_SCHEDULE_THRESHOLD >= this.currentAdDurationS
      ) {
        if (this.isNextAdSlotEmpty === false) {
          // Schedule the next Ad in the current Ad slot
          CastReceiver.log(
            `[LL Manager] Schedule an Ad. nextAdSlotMediaUrls.length=${
              this.nextAdSlotMediaUrls.length
            }`,
          );
          const adUrl = this.nextAdSlotMediaUrls.shift();
          this.chromeCastReceiver.scheduleQueueItem(
            adUrl,
            TRANSITIONS.AD_TO_AD_TRANSITION,
            0,
            this.nextAdSlotMediaDurations[0],
          );
        } else {
          CastReceiver.log(`[LL Manager] Schedule the same event after the Ad slot.`);
          // Schedule current content to play after the Ad slot from the specific position
          this.chromeCastReceiver.scheduleQueueItemMedia(
            cloneDeep(this.currentEventMediaItem),
            TRANSITIONS.AD_TO_LL_TRANSITION,
            this.nextAdStartTimeRelativeS, // This will be the sum of ad slot start relative + all ad lengths
          );
        }
        this.isNextItemScheduled = true;
      }
    } else {
      const { currentPosition } = this;
      if (
        // Check isNextItemScheduled to avoid potential double transition
        // if this block is executed after the onMediaFinished call
        this.isNextItemScheduled === true &&
        this.currentContentDurationS !== undefined &&
        currentPosition >= this.currentContentDurationS
      ) {
        // Trigger event -> event transition
        CastReceiver.log(`[LL Manager] Triggering Event -> Event transition.`);
        this._removeCurrentQueueItem();
        this._onCurrentEventFinished();
      } else if (
        this.isNextItemScheduled === true &&
        this.nextAdStartTimeRelativeS !== undefined &&
        currentPosition >= this.nextAdStartTimeRelativeS
      ) {
        // Trigger event -> ad transition
        this._removeCurrentQueueItem();
        this.currentAdDurationS = this.nextAdSlotMediaDurations.shift();
        this.nextAdSlotAdIndex += 1;
        CastReceiver.log(
          `[LL Manager] Triggering Event -> Ad transition. Ad duration=${this.currentAdDurationS ||
            ''}`,
        );
        this.nextAdStartTimeRelativeS += this.currentAdDurationS;
        this.isNextItemScheduled = false;
      } else if (
        this.isNextItemScheduled === false &&
        ((this.currentContentDurationS !== undefined &&
          currentPosition + NEXT_MEDIA_SCHEDULE_THRESHOLD >= this.currentContentDurationS) ||
          this.isLiveDone)
      ) {
        // Fetch next QVT to schedule an event transition
        CastReceiver.log(`[LL Manager] Fetching NEXT QVT to schedule event transition.`);
        this.isNextItemScheduled = true;
        this.store.dispatch(fetchQvt(this.nextQvtUrl, true));
      } else if (
        this.isNextItemScheduled === false &&
        this.isNextAdSlotEmpty === false &&
        this.nextAdStartTimeRelativeS !== undefined &&
        currentPosition + NEXT_MEDIA_SCHEDULE_THRESHOLD >= this.nextAdStartTimeRelativeS
      ) {
        // Schedule the next Ad item from the upcoming Ad slot
        CastReceiver.log(
          `[LL Manager] Schedule an Ad. nextAdSlotMediaUrls.length=${
            this.nextAdSlotMediaUrls.length
          }`,
        );
        this.isNextItemScheduled = true;
        const adUrl = this.nextAdSlotMediaUrls.shift();
        this.chromeCastReceiver.scheduleQueueItem(
          adUrl,
          TRANSITIONS.LL_TO_AD_TRANSITION,
          0,
          this.nextAdSlotMediaDurations[0],
        );
      }
      this._checkBlackoutStatus();
      this._checkLlRestrictions();
    }
  };

  _getGeoZip = async () => {
    const response = await fetch(GEO_ZIP_URL);
    const geoZip = (await response.json()) || {};
    this.geoZipCode = geoZip.zip_code;
  };

  _mutePlayerVolume = (isMuted = false) => {
    const volumeRequest = new cast.framework.messages.VolumeRequestData();
    volumeRequest.volume.muted = isMuted;
    this.chromeCastReceiver.playerManager.sendLocalMediaRequest(volumeRequest);
  };

  _showBlackoutScreen = () => {
    const blackoutScreen = document.getElementById('blackoutScreen');

    if (blackoutScreen) return;

    const blackoutScreenUrl = `${RECEIVER_APP_PATH}/blackout_image.jpg`;

    const appContainer = document.querySelector('.App-header');
    if (!appContainer) return;

    const imgElement = document.createElement('img');

    imgElement.id = 'blackoutScreen';
    imgElement.src = blackoutScreenUrl;
    imgElement.style.position = 'absolute';
    imgElement.style.zIndex = '999';
    imgElement.style.width = '100%';
    imgElement.style.height = '100%';

    appContainer.appendChild(imgElement);
    this._mutePlayerVolume(true);
  };

  _hideBlackoutScreen = () => {
    const blackoutScreen = document.getElementById('blackoutScreen');
    if (!blackoutScreen) return;
    blackoutScreen.remove();
    this._mutePlayerVolume(false);
  };

  _checkBlackoutStatus = () => {
    const state = this.store.getState();
    const {
      app: { activeReceiver: { zipCode } = {} },
      player: { qvtPayload },
    } = state;

    if (isEmpty(qvtPayload) || !zipCode || !this.geoZipCode) {
      return;
    }

    const {
      player: {
        qvtPayload: {
          playback_info: {
            regional_blackouts: regionalBlackouts = [],
            linear_info: { anchor_time: anchorTime = 0 } = {},
          } = {},
        },
      },
    } = state;

    if (isEmpty(regionalBlackouts)) {
      return;
    }

    const { currentPosition } = this;
    const currentTime = anchorTime + currentPosition;

    const blackout = regionalBlackouts.find(item => {
      const { zip_codes: zipCodes, start_time: startTime, stop_time: stopTime } = item;
      return (
        zipCodes &&
        zipCodes.length &&
        (zipCodes.includes(zipCode) || zipCodes.includes(this.geoZipCode)) &&
        (currentTime > startTime && currentTime < stopTime)
      );
    });

    if (blackout) {
      this._showBlackoutScreen();
    } else {
      this._hideBlackoutScreen();
    }
  };

  _checkLlRestrictions = () => {
    const state = this.store.getState();
    const {
      player: {
        qvtPayload: {
          playback_info: { linear_info: { max_pause_on_live = 0, timeshiftable = true } = {} } = {},
        } = {},
      },
    } = state;
    this.chromeCastReceiver.updateLlConfig(max_pause_on_live > 0, timeshiftable);
  };

  _onAdInfoChange = async (adData: Object) => {
    if (isEmpty(adData)) return;
    const {
      player: {
        qvtPayload: {
          playback_info: {
            ad_info: { ad_breaks: adBreaks },
          },
        },
      },
    } = adData;

    if (isEmpty(adBreaks)) return;
    this._checkNextAdSlot(adBreaks);
  };

  _getAdaptUrlbyAdSlot = (slot: Object) => {
    const state = this.store.getState();

    const {
      player: {
        qvtPayload: { playback_info: qvtPlaybackInfo },
        customAttributesMap,
      },
      auth: { viewer: { analytics_id: analyticsId } = {} },
      networkInfo: { externalPublicIp },
      config: {
        adapt: { vod_url: adaptUrl },
      },
    } = state;

    const customAttribute = customAttributesMap[this.ottMappingId];

    if (!customAttribute) return null;

    const adDuration = slot.stop_offset - slot.start_offset;

    const freewheelAd = getLiveLinearFreeWheelAd(
      customAttribute,
      qvtPlaybackInfo,
      this.currentContentDurationS,
      externalPublicIp,
      'web',
      null,
      analyticsId,
      null,
    );

    freewheelAd.adSlots = [
      {
        slotId: `Slot_0`,
        slotPosition: slot.type.toLowerCase(),
        timePosition: slot.start_offset,
        minDuration: adDuration,
        maxDuration: adDuration,
      },
      // {
      //   // TODO: these lines is workaroud due to issue on adapt server
      //   slotId: `Slot_1`,
      //   slotPosition: slot.type.toLowerCase(),
      //   timePosition: slot.start_offset,
      //   minDuration: adDuration,
      //   maxDuration: adDuration,
      // },
    ];
    return `${adaptUrl}${freewheelAd.url}`;
  };

  _checkNextAdSlot = async (adBreaks: Object, retryCount = 0) => {
    if (retryCount === 0) {
      clearTimeout(this.checkNextAdSlotTimeoutId);
    }
    if (adBreaks.length === this.numberOfAdSlots) {
      return;
    }
    if (this.currentPosition === 0 && retryCount < 2) {
      // This may be an Event -> Event transition and the player may still be loading...
      // Wait a couple of secs and try again
      this.checkNextAdSlotTimeoutId = setTimeout(() => {
        retryCount += 1;
        this._checkNextAdSlot(adBreaks, retryCount);
      }, 2000);
      return;
    }
    this.numberOfAdSlots = adBreaks.length;
    const state = this.store.getState();

    const {
      player: {
        qvtPayload: {
          playback_info: {
            linear_info: { anchor_time: contentStartAbsolute },
          },
        },
      },
    } = state;

    const nextAdBreak = adBreaks[adBreaks.length - 1];
    const adSlotStartRelative = nextAdBreak.start_offset - contentStartAbsolute;

    CastReceiver.log(
      `[LL Manager] adSlotStartRelative - playerManager.getCurrentTimeSec()=${adSlotStartRelative -
        this.currentPosition}, time=${new Date().toString()}`,
    );

    if (adSlotStartRelative < this.currentPosition) {
      return;
    }

    const url = this._getAdaptUrlbyAdSlot(nextAdBreak);
    if (!url) return;

    const response = await fetch(url);
    const adsResponse = await response.text();
    if (!adsResponse) return;

    this.nextAdSlotAdIndex = 0;
    this.nextAdSlotsInfo = new AdSlotInfo(adsResponse);
    if (this.isNextAdSlotEmpty) return;

    this.chromeCastReceiver.setLiveLinearAdSlotInfoForAnalytics(this.nextAdSlotsInfo.adSlotInfo);
    this.nextAdStartTimeRelativeS = adSlotStartRelative;
    CastReceiver.log(
      `[LL Manager] Received Ad slot Ads. nextAdSlotMediaUrls.length=${
        this.nextAdSlotMediaUrls.length
      }, nextAdStartTimeRelativeS=${this.nextAdStartTimeRelativeS}`,
    );
    CastReceiver.log(
      `[LL Manager] nextAdSlotMediaUrls: ${JSON.stringify(this.nextAdSlotMediaUrls)}`,
    );
    CastReceiver.log(
      `[LL Manager] nextAdSlotMediaDurations: ${JSON.stringify(this.nextAdSlotMediaDurations)}`,
    );
  };
}
